import {
  ProductReminderListParams,
  ProductReminderPageResponse,
  ProductReminderWriteData,
} from "@/types/productReminder";
import { adminUrl } from "@/utils/url";
import { HttpClient, HttpError } from "../HttpClient";
import { toServiceError } from "../errors";
import { IProductRemindersService } from "./IProductRemindersService";

const SKU_NOT_FOUND = "PRODUCT_REMINDER_SKU_NOT_FOUND";

/**
 * A save refused because some SKUs are not active catalog items. Carries the
 * ones the API named, so the form can point at them.
 */
export class ProductReminderSkusNotFoundError extends Error {
  readonly skus: string[];

  constructor(message: string, skus: string[]) {
    super(message);
    this.name = "ProductReminderSkusNotFoundError";
    this.skus = skus;
  }
}

/**
 * The SKUs a `PRODUCT_REMINDER_SKU_NOT_FOUND` names.
 *
 * The API lists them only inside its free-text `message`, so rather than
 * parsing a sentence whose wording may change, the submitted SKUs are matched
 * against the message's words. A SKU that is a substring of another word does
 * not count.
 */
const missingSkusOf = (error: HttpError, submitted: string[]): string[] => {
  const message = error.responseData?.message;
  if (typeof message !== "string") return [];

  const words = new Set(
    message
      .split(/[\s,;:()[\]{}"'«»]+/)
      .map(word => word.replace(/\.+$/, ""))
      .filter(Boolean),
  );

  return [...new Set(submitted.map(sku => sku.trim()))].filter(sku =>
    words.has(sku),
  );
};

export class ProductRemindersService implements IProductRemindersService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: `${adminUrl}/refProductReminders`,
      timeout: 10000,
      headers: {},
    });
  }

  async getAll({
    sku,
    communicationTypeId,
    templateId,
    page = 0,
    size = 20,
    signal,
  }: ProductReminderListParams = {}): Promise<ProductReminderPageResponse> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sort: "id,desc",
    });

    if (sku?.trim()) queryParams.set("sku", sku.trim());
    if (communicationTypeId !== undefined) {
      queryParams.set("communicationTypeId", String(communicationTypeId));
    }
    if (templateId !== undefined) {
      queryParams.set("templateId", String(templateId));
    }

    try {
      return await this.httpClient.get<ProductReminderPageResponse>(
        `?${queryParams.toString()}`,
        signal ? { signal } : {},
      );
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") throw error;

      throw toServiceError(
        error,
        "Não foi possível carregar os recordatórios de produtos. Atualize a página e tente novamente.",
      );
    }
  }

  async create(data: ProductReminderWriteData): Promise<number> {
    try {
      return await this.httpClient.post<number>(``, data, {}, true);
    } catch (error) {
      throw this.toWriteError(
        error,
        data,
        "Não foi possível criar o recordatório. Confirme os campos obrigatórios e tente novamente.",
      );
    }
  }

  async update(id: number, data: ProductReminderWriteData): Promise<void> {
    try {
      await this.httpClient.put<void>(`/${id}`, data);
    } catch (error) {
      throw this.toWriteError(
        error,
        data,
        "Não foi possível atualizar o recordatório. Tente guardar novamente.",
      );
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await this.httpClient.delete<void>(`/${id}`);
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível eliminar o recordatório. Tente novamente.",
      );
    }
  }

  /**
   * A write's error, translated. Two cases say more than the shared table can:
   * unknown SKUs are named, and a 404 — the template is gone or inactive, or
   * the reminder itself was deleted — gets a message about this form rather
   * than the generic "resource not found".
   */
  private toWriteError(
    error: unknown,
    data: ProductReminderWriteData,
    defaultMessage: string,
  ): Error {
    if (error instanceof HttpError) {
      if (error.responseData?.errorCode === SKU_NOT_FOUND) {
        const skus = missingSkusOf(error, data.skus);
        return new ProductReminderSkusNotFoundError(
          skus.length === 1
            ? `O SKU ${skus[0]} não corresponde a nenhum artigo ativo do catálogo. Corrija-o e guarde novamente.`
            : skus.length > 1
              ? `Estes SKUs não correspondem a nenhum artigo ativo do catálogo: ${skus.join(", ")}. Corrija-os e guarde novamente.`
              : toServiceError(error, defaultMessage).message,
          skus,
        );
      }

      if (error.status === 404) {
        return new Error(
          "O modelo escolhido já não está ativo, ou o recordatório foi eliminado. Atualize a lista e tente novamente.",
        );
      }
    }

    return toServiceError(error, defaultMessage);
  }
}
